import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Sidebar from "../Dashboard/Sidebar.js";
import Stack from "@mui/material/Stack";
import { Box } from "@mui/system";
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  CircularProgress,
  useMediaQuery,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import Paper from "@mui/material/Paper";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import Typography from "@mui/material/Typography";
import { ToastContainer, toast } from "react-toastify";
import { Add, DeleteForever, Edit, Search } from "@mui/icons-material";
import { getSeoColor } from "../../utils/seo/calculateSeoScore";
import { blogService } from "../../services/blogService";

// --- STYLED COMPONENTS (matching Inventory.js / CarFacts.js) ---

const RootContainer = styled(Box)({
  background: "#F8FAFC",
  minHeight: "100vh",
  display: "flex",
});

const ContentArea = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  marginLeft: "18vw",
  padding: "40px",
  width: "calc(100% - 18vw)",
  transition: "all 0.3s ease",
  [theme.breakpoints.down("md")]: {
    marginLeft: 0,
    width: "100%",
    padding: "20px",
    paddingTop: "84px",
  },
}));

const GlassCard = styled(Paper)({
  padding: "24px",
  borderRadius: "16px",
  background: "#FFFFFF",
  border: "1px solid #E2E8F0",
  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
});

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  border: "none",
  backgroundColor: "#FFFFFF",
  borderRadius: "16px",
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "#F1F5F9",
    color: "#475569",
    fontWeight: 700,
    fontSize: "14px",
    borderBottom: "1px solid #E2E8F0",
  },
  "& .MuiDataGrid-cell": {
    borderBottom: "1px solid #F1F5F9",
    color: "#1E293B",
  },
  "& .MuiDataGrid-footerContainer": {
    borderTop: "1px solid #E2E8F0",
  },
  [theme.breakpoints.down("sm")]: {
    "& .MuiDataGrid-columnHeaders": {
      fontSize: "12px",
    },
    "& .MuiDataGrid-cell": {
      fontSize: "0.8rem",
      paddingTop: 6,
      paddingBottom: 6,
    },
  },
}));

const FilterCard = styled(Paper)({
  padding: "16px 24px",
  borderRadius: "16px",
  background: "#FFFFFF",
  border: "1px solid #E2E8F0",
  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
  marginBottom: "24px",
});

// --- COMPONENT ---

function Blogs() {
  return (
    <RootContainer>
      <Sidebar />
      <BlogList />
    </RootContainer>
  );
}

const BlogList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  // Master blog state
  const [blogs, setBlogs] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [rowCount, setRowCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  // Filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("All");

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [blogToDelete, setBlogToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const isSuperAdmin = localStorage.getItem("superadmin") === "true";

  // Fetch blogs on mount
  const fetchBlogs = async (pageToLoad = page, pageSizeToLoad = pageSize) => {
    setIsLoading(true);
    setIsError(false);
    try {
      const response = await blogService.getBlogs(
        pageToLoad + 1,
        pageSizeToLoad
      );
      if (response?.success && response.data) {
        setBlogs(response.data);
        if (response.pagination?.total != null) {
          setRowCount(response.pagination.total);
        } else {
          setRowCount(response.data.length);
        }
      } else {
        setIsError(true);
        toast.error(response?.message || "Failed to fetch blogs from API.");
      }
    } catch (err) {
      setIsError(true);
      toast.error("Failed to fetch blogs from API.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogs(page, pageSize);
  }, [page, pageSize]);

  // Listen for incoming blog data from the editor (create or edit) - mostly handled by API re-fetch now, but keeping for UX immediacy
  useEffect(() => {
    if (location.state?.blogData) {
      // Re-fetch to ensure sync with DB
      fetchBlogs(page, pageSize);
      window.history.replaceState({}, document.title);
    }
  }, [location.state, page, pageSize]);

  // Derived categories
  const categories = useMemo(() => {
    const cats = [...new Set(blogs.map((b) => b.category))];
    return ["All", ...cats.sort()];
  }, [blogs]);

  // Filtered rows
  const filteredRows = useMemo(() => {
    let result = [...blogs];

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (b) =>
          (b.title && b.title.toLowerCase().includes(q)) ||
          (b.category && b.category.toLowerCase().includes(q))
      );
    }

    if (categoryFilter !== "All") {
      result = result.filter((b) => b.category === categoryFilter);
    }

    // Newest blogs first by createdAt (fallback to id)
    // Server already returns sorted by createdAt DESC

    return result;
  }, [blogs, searchQuery, categoryFilter]);

  // Delete handlers
  const handleDeleteClick = (blog) => {
    setBlogToDelete(blog);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (blogToDelete) {
      setIsDeleting(true);
      try {
        await blogService.deleteBlog(blogToDelete.id);
        setBlogs((prev) => prev.filter((b) => b.id !== blogToDelete.id));
        toast.success("Blog deleted successfully!");
        setDeleteDialogOpen(false);
        setBlogToDelete(null);
      } catch (err) {
        toast.error("Failed to delete blog. Please try again.");
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setBlogToDelete(null);
  };

  // DataGrid columns (matching Inventory.js pattern) with responsive visibility
  const columns = useMemo(() => {
    const actionsColumn = {
      field: "actions",
      headerName: "Actions",
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <IconButton
            size="small"
            color="primary"
            onClick={() => navigate(`/blogs/edit/${params.row.slug}`)}
            sx={{
              border: "1px solid",
              borderColor: "primary.main",
              borderRadius: "8px",
              "&:hover": { bgcolor: "#eff6ff" },
            }}
          >
            <Edit fontSize="small" />
          </IconButton>
          {isSuperAdmin && (
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDeleteClick(params.row)}
              sx={{
                border: "1px solid",
                borderColor: "error.main",
                borderRadius: "8px",
                "&:hover": { bgcolor: "#ffe6e6" },
              }}
            >
              <DeleteForever fontSize="small" />
            </IconButton>
          )}
        </Stack>
      ),
    };

    const idColumn = {
      field: "id",
      headerName: "ID",
      width: 80,
    };

    const titleColumn = {
      field: "title",
      headerName: "Title",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Typography
          sx={{
            fontWeight: 500,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {params.value}
        </Typography>
      ),
    };

    const categoryColumn = {
      field: "category",
      headerName: "Category",
      width: 140,
      renderCell: (params) => (
        <Chip
          label={params.value || "N/A"}
          size="small"
          sx={{
            fontWeight: 600,
            backgroundColor: "#F1F5F9",
            color: "#475569",
          }}
        />
      ),
    };

    const seoColumn = {
      field: "seoScore",
      headerName: "SEO Score",
      width: 120,
      type: "number",
      renderCell: (params) => {
        const score = params.value;
        if (!score || score === 0) {
          return (
            <Chip
              label="N/A"
              size="small"
              sx={{
                fontWeight: 600,
                backgroundColor: "#F1F5F9",
                color: "#94A3B8",
                fontSize: "0.75rem",
              }}
            />
          );
        }
        return (
          <Chip
            label={`${score}/100`}
            size="small"
            sx={{
              fontWeight: 700,
              backgroundColor: getSeoColor(score),
              color: "#FFFFFF",
              fontSize: "0.75rem",
            }}
          />
        );
      },
    };

    const createdAtColumn = {
      field: "createdAt",
      headerName: "Created Date",
      width: 140,
      valueFormatter: (params) => {
        const val = params.value;
        if (!val) return "-";
        return new Date(val).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        });
      },
    };

    if (isMobile) {
      // Mobile: Actions | Title | SEO Score
      return [actionsColumn, titleColumn, seoColumn];
    }

    if (isTablet) {
      // Tablet: Actions | Title | Category | SEO Score | Created Date
      return [actionsColumn, titleColumn, categoryColumn, seoColumn, createdAtColumn];
    }

    // Desktop: Actions | ID | Title | Category | SEO Score | Created Date
    return [
      actionsColumn,
      idColumn,
      titleColumn,
      categoryColumn,
      seoColumn,
      createdAtColumn,
    ];
  }, [isMobile, isTablet, navigate]);

  return (
    <ContentArea>
      {/* ── HEADER ── */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        spacing={3}
        sx={{ mb: 4 }}
      >
        <Box>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              color: "#1E293B",
              fontFamily: "Kamerik",
              letterSpacing: "-1.5px",
              fontSize: { xs: "2rem", md: "3rem" },
            }}
          >
            Blogs
          </Typography>
        </Box>

        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate("/blogs/create")}
          sx={{
            borderRadius: "12px",
            px: 3,
            bgcolor: "#1E293B",
            fontWeight: 700,
            height: "48px",
            textTransform: "none",
            "&:hover": { bgcolor: "#0F172A" },
            width: { xs: "100%", md: "auto" },
          }}
        >
          Create Blog
        </Button>
      </Stack>

      {/* ── FILTERS ── */}
      <FilterCard elevation={0}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search blogs by title or category..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <Search sx={{ color: "#94A3B8", mr: 1, fontSize: 20 }} />
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  backgroundColor: "#F8FAFC",
                  "& fieldset": { borderColor: "#E2E8F0" },
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              size="small"
              label="Category"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  backgroundColor: "#F8FAFC",
                  "& fieldset": { borderColor: "#E2E8F0" },
                },
              }}
            >
              {categories.map((cat) => (
                <MenuItem key={cat} value={cat}>
                  {cat}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </FilterCard>

      {/* ── DATA TABLE ── */}
      {isError ? (
        <Paper sx={{ p: 4, textAlign: "center", borderRadius: "16px" }}>
          <Typography color="error">
            Failed to load blogs. Is the API server running on port 8080?
          </Typography>
          <Button onClick={fetchBlogs} variant="outlined" sx={{ mt: 2 }}>
            Retry
          </Button>
        </Paper>
      ) : (
        <GlassCard elevation={0}>
          <StyledDataGrid
            autoHeight
            loading={isLoading}
            rows={filteredRows}
            columns={columns}
            getRowId={(row) => row.id}
            pagination
            paginationMode="server"
            page={page}
            pageSize={pageSize}
            onPageChange={(newPage) => setPage(newPage)}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
              setPage(0);
            }}
            rowCount={rowCount}
            rowsPerPageOptions={[10, 25, 50]}
            disableRowSelectionOnClick
            disableSelectionOnClick
            components={{
              Toolbar: GridToolbar,
            }}
            componentsProps={{
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 500 },
              },
            }}
            sx={{
              "& .MuiDataGrid-toolbarContainer": {
                padding: "16px",
                gap: "12px",
                borderBottom: "1px solid #E2E8F0",
                "& .MuiButton-root": {
                  color: "#64748B",
                  fontWeight: 700,
                  "&:hover": { color: "#1E293B" },
                },
              },
            }}
            slots={{
              noRowsOverlay: () => (
                <Box
                  sx={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    p: 2,
                  }}
                >
                  <Typography sx={{ color: "#64748B", mb: 1 }}>
                    No blogs found. Create your first blog.
                  </Typography>
                </Box>
              ),
            }}
          />
        </GlassCard>
      )}

      {/* ── DELETE CONFIRMATION DIALOG ── */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            padding: "8px",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: "#1E293B" }}>
          Delete Blog
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "#475569" }}>
            Are you sure you want to delete "
            <strong>{blogToDelete?.title}</strong>"? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseDeleteDialog}
            sx={{
              borderRadius: "10px",
              color: "#64748B",
              fontWeight: 600,
              textTransform: "none",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            disabled={isDeleting}
            sx={{
              borderRadius: "10px",
              fontWeight: 600,
              textTransform: "none",
            }}
          >
            {isDeleting ? <CircularProgress size={24} color="inherit" /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <ToastContainer position="bottom-right" />
    </ContentArea>
  );
};

export default Blogs;
